import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiActivity, FiInfo, FiUsers, FiCpu} from 'react-icons/fi';
import { Card } from '../ui/Card';
import { Icon } from '../ui/Icon';
import { theme } from '../../styles/theme';

const FeaturesSection = styled.section`
  padding: ${theme.spacing['5xl']} 0;
  background: ${theme.colors.neutral};
  scroll-margin-top: 80px; /* Account for fixed navbar */
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['4xl']};
`;

const SectionTitle = styled(motion.h2)`
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.lg};
`;

const SectionSubtitle = styled(motion.p)`
  font-size: ${theme.typography.sizes.xl};
  color: ${theme.colors.gray[600]};
  max-width: 600px;
  margin: 0 auto;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: ${theme.spacing['2xl']};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.xl};
  }
`;

const FeatureCard = styled(Card)`
  text-align: center;
  height: 100%;
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: ${theme.colors.gradients.hero};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing.xl} auto;
  
  svg {
    width: 40px;
    height: 40px;
    color: ${theme.colors.white};
  }
`;

const FeatureTitle = styled.h3`
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.md};
`;

const FeatureDescription = styled.p`
  color: ${theme.colors.gray[600]};
  line-height: ${theme.typography.lineHeights.relaxed};
`;

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

export const Features: React.FC = () => {
  return (
    <FeaturesSection id="features">
      <Container>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Comprehensive Care Management
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Everything you need to deliver exceptional disability and aged care, 
            all in one intelligent platform.
          </SectionSubtitle>
        </SectionHeader>
        
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <FeaturesGrid>
            <motion.div variants={cardVariants}>
              <FeatureCard hover>
                <FeatureIcon>
                  <Icon icon={FiActivity} />
                </FeatureIcon>
                <FeatureTitle>Real-Time Patient Tracking</FeatureTitle>
                <FeatureDescription>Monitor sleep, meals, medication and daily routines all in one place!</FeatureDescription>
              </FeatureCard>
            </motion.div>
          
            <motion.div variants={cardVariants}>
              <FeatureCard hover>
                <FeatureIcon>
                  <Icon icon={FiInfo} />
                </FeatureIcon>
                <FeatureTitle>Transparent Communication</FeatureTitle>
                <FeatureDescription>Families, caregivers, nurses, organisations – everyone stays informed.</FeatureDescription> 
              </FeatureCard>
            </motion.div>
            
            <motion.div variants={cardVariants}>
              <FeatureCard hover>
                <FeatureIcon>
                  <Icon icon={FiUsers} />
                </FeatureIcon>
                <FeatureTitle>Streamlined Care Coordination</FeatureTitle>
                <FeatureDescription>Assign tasks, adjust treatments, manage care plans effortlessly.</FeatureDescription>
              </FeatureCard>
            </motion.div>

            <motion.div variants={cardVariants}>
              <FeatureCard hover>
                <FeatureIcon>
                  <Icon icon={FiCpu} />
                </FeatureIcon>
                <FeatureTitle>AI-Powered Analysis (Coming Soon)</FeatureTitle>
                <FeatureDescription>Predictive analytics to improve patient care and detect early risks based on historical data.</FeatureDescription>
              </FeatureCard>
            </motion.div>
          </FeaturesGrid>
        </motion.div>
      </Container>
    </FeaturesSection>
  );
};